// @flow
import React from 'react'
import { Link } from 'react-router-dom'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import { AccountExtrasRef, ClosingRef, GPTDatesRef, GPTLineItemsRef, InvestmentsRef, SSIRef } from 'components/References'
import { Notes } from 'components/Notes'
import { Table, Row as TableRow, Cell } from 'components/Table'
import formatters from 'lib/formatters'
import { setModal } from 'lib/modal/actions'
import type { Account } from 'lib/types'
import styles from './styles'
import { Dialog } from 'components/Dialog'
import ClosingTrackForm from './ClosingTrackForm'
import DigitalFile from './DigitalFile'
import Main from './Tabs/Main/Main.js'
import Metrics from './Tabs/Metrics/Metrics.js'
import People from './Tabs/People/People.js'
import Header from './Header/Header.js'
import StatusDialog from '../Statusdialog/Statusdialog.js'

type Props = {
  account: Account,
  dispatch: Function,
  isAuditor: boolean,
  userRoles: Array<string>,
  showAccountStatusDialog: Function,
  hideAccountStatusDialog: Function,
  handleStatusChange: Function,
  showStatusDialog: boolean,
}

type DataProps = {
  data: Object,
  index: number,
}

const accountExtras = (account) => {
  return (
    <AccountExtrasRef
      status={account['status']}
      ageAtOpen={account['age_at_open']}
      hasCustomFeeSchedule={account['fee_schedule'] && account['fee_schedule'].includes('-')}
      hasNonCashAssets={account['non_cash_assets?']}
      isBurialPrepaid={account['prepaid_burial?']}
      isRestricted={account['restricted?']}
      potentialClientId={account['potential_client_id']}
      trustName={account['trust_name']}
      productDescription={account['product_description']}
      hasBudget={account['budget?']}
      hasPublicGuardian={account['public_guardian?']}
      hasDonationClient={account['donation_client']}
      hasSpecialTaxSituation={account['special_tax_situation?']}
      spendingPlan={account['spending_plan']}
      floor={account['floor']}
      hasParticipatingOrg={account['participating_org?']}
      hasLien={account['lien?']}
      hasTeams={account['teams?']}
      hasGTHiredCareManagement={account['gt_hired_care_management?']}
      pCardLast4={account['p_card_last_4']}
      isQIT={account['qit']}
      hasSubmissionToJurisdiction={account['submission_to_jurisdiction_of_court']}
      guardianshipAdminCounty={account['guardianship_administration_county']}
      initialDepositAmount={account['initial_deposit_amount']}
      monthlyIncomeAmount={account['monthly_income_amount']}
    />
  )
}

const openClosingFeeModal = (account, dispatch) => {
  const modalForm = <ClosingTrackForm accountNumber={account.number} dispatch={dispatch} />
  dispatch(setModal(modalForm))
}

const renderTickets = (props: DataProps) => {
  const { data, index } = props
  let { id, track, station, updated_at, last_event } = data
  track = formatters.legibleTrack(track)
  station = formatters.legibleStation(station)
  const lastUser = <span className="capitalize">{last_event.user.split('@', 1)}</span>
  const description = <span>{last_event.description}</span>
  const updatedTime = <span className={styles.updateTime}>({formatters.naiveDateTime(updated_at).fromNow()})</span>
  const updated = (
    <div className={styles.recentActivity}>
      {lastUser} {description} {updatedTime}
    </div>
  )

  return (
    <TableRow key={index} link={`/tickets/${id}`}>
      <Cell value={track} />
      <Cell value={station} />
      <Cell value={updated} />
    </TableRow>
  )
}

export const Template = (props: Props) => {
  const {
    account,
    dispatch,
    isAuditor,
    userRoles,
    showAccountStatusDialog,
    hideAccountStatusDialog,
    handleStatusChange,
    showStatusDialog,
  } = props

  const {
    ables,
    attorneys,
    balance,
    beneficiary,
    checking_balance,
    close_date,
    co_grantor,
    consults,
    grantor,
    grantor_relationship,
    id,
    investments,
    notes,
    number,
    open_date,
    open_deposits,
    open_tickets,
    product_id,
    referrer,
    related_account_numbers = [],
    remaindermen,
    remaindermen_notes,
    representative,
    short_code,
    subaccounts,
    status,
    transactions = [],
    trust_name,
    updatedAt,
  } = account

  const hasTransactions = transactions.length > 0

  let editButton = isAuditor ? null : (
    <Link className={styles.editButton} disabled={true} to={`/accounts/${number}/edit`}>
      Edit Account
    </Link>
  )

  const reportButton = (
    <Link className={styles.statementButton} to={`/accounts/${number}/statement`}>
      Generate Statement
    </Link>
  )

  let closingFeeButton =
    status === 'approved' && !isAuditor ? (
      <Link className={styles.closingButton} onClick={() => openClosingFeeModal(account, dispatch)}>
        Begin Closing Track
      </Link>
    ) : null

  return (
    <section className={styles.contentContainer}>
      {/* Tabs showing dynamic content on the left */}
      <section className={styles.twoThird}>
        <Header
          balance={balance}
          beneficiary={beneficiary}
          checking_balance={checking_balance}
          number={number}
          open_deposits={open_deposits}
          open_tickets={open_tickets}
          related_account_numbers={related_account_numbers}
          status={status}
          sub_accounts={subaccounts}
        />
        <Tabs defaultIndex={0}>
          <TabList className={[styles.tabList, 'react-tabs']}>
            <Tab className={[styles.buttonLocal, 'react-tabs__tab']}>Main</Tab>
            <Tab className={[styles.buttonLocal, 'react-tabs__tab']}>Transactions</Tab>
            <Tab className={[styles.buttonLocal, 'react-tabs__tab']}>Attachments</Tab>
            <Tab className={[styles.buttonLocal, 'react-tabs__tab']}>People</Tab>
            <Tab className={[styles.buttonLocal, 'react-tabs__tab']}>Investments</Tab>
            <Tab disabled={!hasTransactions} className={[styles.buttonLocal, hasTransactions ? '' : styles.disabled, 'react-tabs__tab']}>
              Metrics
            </Tab>
          </TabList>
          <TabPanel>
            <Main
              beneficiary={beneficiary}
              co_grantor={co_grantor}
              grantor={grantor}
              grantor_relationship={grantor_relationship}
              number={number}
              representative={representative}
              subaccounts={subaccounts}
            />
          </TabPanel>
          <TabPanel>
            <GPTLineItemsRef accountNumber={number} shortCode={short_code} trustName={trust_name} lineItems={[...transactions].reverse()} />
          </TabPanel>
          <TabPanel>
            <DigitalFile accountNumber={number} />
          </TabPanel>
          <TabPanel>
            <People
              ables={ables}
              attorneys={attorneys}
              consults={consults}
              referrer={referrer}
              remaindermen={remaindermen}
              remaindermen_notes={remaindermen_notes}
            />
          </TabPanel>
          <TabPanel>
            <InvestmentsRef account_id={id} history={investments.history} next_change_date={investments.next_change_date} product_id={product_id} />
          </TabPanel>
          <TabPanel>
            <Metrics accountNumber={number} transactions={transactions} />
          </TabPanel>
        </Tabs>
      </section>

      {/* Static content on the right */}
      <div className={styles.oneThird}>
        <div className={styles.actions}>
          {editButton}
          {reportButton}
          {closingFeeButton}
        </div>
        <div className={styles.cardMarginBot}>
          <GPTDatesRef
            closeDate={close_date}
            openDate={open_date}
            status={status}
            updatedAt={updatedAt}
            userRoles={userRoles}
            showAccountStatusDialog={showAccountStatusDialog}
          />
          {accountExtras(account)}
        </div>
        {['closing', 'closed'].includes(account.status) && <ClosingRef collapse={true} account={account} beneficiary={beneficiary} />}
        <SSIRef
          collapse={true}
          hasSSI={account['ssi?']}
          hasSSIShelter={account['ssi_shelter?']}
          ssiAmount={account.ssi_amount}
        />
        <Notes accountNumber={number} dispatch={dispatch} notes={notes} />
      </div>

      {/* Status Dialog */}
      {showStatusDialog && <StatusDialog account={account} userRoles={userRoles} onCancel={hideAccountStatusDialog} onConfirm={handleStatusChange} />}
    </section>
  )
}
